/**
 * Multi-select picker for Pulse event types.
 *
 * Shared between the webhook create page and the Events tab on the
 * detail page. Renders the platform catalog grouped by entity in a
 * bounded-height scroll panel with sticky group headers, plus a
 * checkbox per event row.
 *
 * Stateless about which events are selected - parent owns the Set
 * and toggle handler. Loading is handled inline so callers don't
 * have to thread the query state through.
 */

import { Loader2 } from 'lucide-react'

import {
  groupEventsByEntity,
  usePulseEventTypes,
} from '@/admin/hooks/use-pulse-events'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { cn } from '@/shared/lib/utils'

export interface EventMultiSelectProps {
  selected: Set<string>
  onToggle: (eventKey: string) => void
  maxHeight?: string
}

export function EventMultiSelect({
  selected,
  onToggle,
  maxHeight = 'max-h-[420px]',
}: EventMultiSelectProps) {
  const events = usePulseEventTypes()
  const rows = events.data ?? []
  const groups = groupEventsByEntity(rows)

  if (events.isLoading) {
    return (
      <div className="flex items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-10">
        <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]',
        maxHeight,
      )}
    >
      {groups.map((group, gIdx) => (
        <div key={group.key}>
          {gIdx > 0 && (
            <div className="h-px bg-[var(--rule-light)]" aria-hidden />
          )}
          <div className="sticky top-0 z-10 bg-[var(--bg-subtle)] px-3 py-1.5 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            {group.label}
          </div>
          {group.events.map((evt) => {
            const checked = selected.has(evt.id)
            return (
              <label
                key={evt.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 border-t-[0.5px] border-t-[var(--rule-light)] px-3 py-2.5 first:border-t-0',
                  checked && 'bg-[var(--green-bg-subtle)]',
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(evt.id)}
                />
                <span className="flex-1 text-[12.5px] text-[var(--ink)]">
                  {evt.label}
                </span>
                <span className="hidden text-[11px] font-light text-[var(--ink-3)] md:inline">
                  {evt.description}
                </span>
              </label>
            )
          })}
        </div>
      ))}
    </div>
  )
}
